// Campaigns of a brand: the workspace's single brand by default, or picked.
import type { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Megaphone, Plus } from "lucide-react";
import { useState } from "react";
import { api, call } from "@/api/client";
import { keys, useFrontDesk } from "@/api/hooks";
import type { Campaign } from "@/api/types";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { EmptyState, PageHeader, Section } from "@/ui/layout";
import { DataTable } from "@/components/data-table";
import { NameDialog } from "@/components/name-dialog";
import { ContextPicker } from "../common/context-picker";
import { PageError, PageLoading, useTitle, useRouteParams, useSearchRecord } from "../common/page";
import { useCampaigns } from "../common/queries";

const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : undefined);

export function campaignColumns(base: string): Array<ColumnDef<Campaign>> {
  return [
    {
      header: "Campaña",
      cell: ({ row }) => (
        <Link to={`${base}/campaigns/${row.original.id}`} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    {
      header: "Estado",
      cell: ({ row }) =>
        row.original.archived ? (
          <Badge>Archivada</Badge>
        ) : row.original.paused ? (
          <Badge tone="warning">Pausada</Badge>
        ) : (
          <Badge tone="success">Activa</Badge>
        ),
    },
    { header: "Creada", cell: ({ row }) => formatDateTime(row.original.createdAt) },
  ];
}

export function CampaignsPage() {
  const { orgId } = useRouteParams("orgId");
  const search = useSearchRecord();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: fd, isLoading, error } = useFrontDesk(orgId);
  useTitle("Campañas");
  const picked = {
    clientId: str(search.clientId) ?? fd?.creationContext?.clientId,
    brandId: str(search.brandId) ?? fd?.creationContext?.brandId,
  };
  const campaigns = useCampaigns(orgId, picked.brandId);
  if (isLoading) return <PageLoading />;
  if (error || !fd) return <PageError error={error} orgId={orgId} />;
  const base = `/o/${orgId}`;
  const simple = Boolean(fd.creationContext) && !str(search.brandId);
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Campañas"
        actions={
          fd.capabilities.manageCampaigns && picked.brandId ? (
            <Button
              variant="primary"
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              <Plus /> Nueva campaña
            </Button>
          ) : null
        }
      />
      <Section title={simple ? "Tus campañas" : "Por marca"}>
        {!simple ? (
          <ContextPicker
            orgId={orgId}
            levels={2}
            value={picked}
            onChange={(next) =>
              void navigate({ to: `${base}/campaigns`, search: { clientId: next.clientId, brandId: next.brandId } })
            }
          />
        ) : null}
        {picked.brandId ? (
          <DataTable
            label="Campañas"
            columns={campaignColumns(base)}
            rows={campaigns.items}
            loading={campaigns.isLoading}
            hasMore={campaigns.hasNextPage}
            loadingMore={campaigns.isFetchingNextPage}
            onLoadMore={() => void campaigns.fetchNextPage()}
            empty={
              <EmptyState
                icon={<Megaphone />}
                title="Aún no hay campañas"
                description="Al crear tu primer creativo preparamos una campaña por ti."
              />
            }
          />
        ) : (
          <EmptyState icon={<Megaphone />} title="Elige una marca" />
        )}
      </Section>
      <NameDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Nueva campaña"
        label="Nombre de la campaña"
        submitLabel="Crear campaña"
        onSubmit={async (name) => {
          const created=await call(() =>
            api.POST("/orgs/{orgId}/brands/{brandId}/campaigns", {
              params: { path: { orgId, brandId: picked.brandId ?? "" } },
              body: { name },
            })
          );
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
          await navigate({to:`${base}/campaigns/${created.id}`});
        }}
      />
    </div>
  );
}
